import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Apollo } from "apollo-angular";
import dayjs from "dayjs";
import { combineLatest, map } from "rxjs";

import { Location, formatDate } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";

import {
  Impact,
  MajorDisruptionNotificationFilter,
  NotificationFilter,
  NotificationType,
  TicketType,
} from "../../apis/networkdashboard/graphql/graphql";
import { CimMajorDisruptionResult, CimResult, CimStatus } from "../../apis/networkdashboard/static/custom-types";
import {
  CIM_MAJOR_DISRUPTION_QUERY,
  CIM_QUERY,
} from "../../apis/networkdashboard/static/graphql-queries/cim-notifications";
import { FooterComponent } from "../../components/common/footer/footer";
import { LoaderComponent } from "../../components/common/loader/loader";
import { NavigationComponent } from "../../components/common/navigation/navigation";
import { MalfunctionCardComponent } from "../../components/malfunction/malfunction-card/malfunction-card";
import { MalfunctionFilter } from "../../components/malfunction/malfunction-filter/malfunction-filter";
import { TimelineFooterComponent } from "../../components/malfunction/timeline-footer/timeline-footer";
import { Webarchive } from "../../components/malfunction/webarchive/webarchive";
import { groupByAsSortedArray } from "../../helpers/group-by";
import { subscriptionNameKey } from "../../helpers/subscription-i18n";
import { MalfunctionFilterType } from "../../helpers/types";
import { AuthService } from "../../services/authservice";
import { MessageService } from "../../services/messageservice";
import { SortableNotification, reverseSortTickets } from "./malfunction.helpers";

@Component({
  selector: "page-malfunction",
  templateUrl: "malfunction.html",
  styleUrls: ["malfunction.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FooterComponent,
    LoaderComponent,
    MalfunctionCardComponent,
    MalfunctionFilter,
    NavigationComponent,
    TimelineFooterComponent,
    TranslatePipe,
    Webarchive,
  ],
})
export class MalfunctionPage {
  private auth = inject(AuthService);
  private msgsrv = inject(MessageService);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private apollo = inject(Apollo);
  private translate = inject(TranslateService);

  private filterData = signal<MalfunctionFilterType | undefined>(undefined);
  private activeTypeFilters = signal<string[]>([]);
  private activeImpactFilters = signal<string[]>([]);
  protected presetSubscriptionId = signal(this.route.snapshot.paramMap.get("subscriptionId") ?? "");
  protected presetProductType = signal(this.route.snapshot.paramMap.get("productType") ?? "");
  protected loading = true;
  private customerGuid: string;
  protected hasMore = true;

  private loadMoreActive = false;

  private tickets = signal<NotificationType[]>([]);
  private majorDisruptionTickets = computed(() =>
    this.tickets().filter((ticket) => ticket.type === TicketType.MajorDisruption),
  );
  protected majorDisruptionTicketsOpen = computed(() =>
    this.majorDisruptionTickets().filter((ticket) => ticket.status !== CimStatus.CLOSED),
  );
  private majorDisruptionTicketsClosed = computed(() =>
    this.majorDisruptionTickets().filter((ticket) => ticket.status === CimStatus.CLOSED),
  );
  protected filteredTicketsActive = computed(() =>
    this.filteredTickets()
      .filter((ticket) => ticket.type !== TicketType.MajorDisruption && ticket.status !== CimStatus.CLOSED)
      .filter((ticket) => this.isCurrentlyActive(ticket)),
  );
  protected filteredTicketsInactive = computed(() => {
    const grouper = (ticket: SortableNotification<NotificationType>) =>
      formatDate(ticket.startDate, "yyyy-MM-dd", "en-US");
    const sorter = (a: string, b: string) => b.localeCompare(a);
    const inactiveTickets = this.filteredTickets()
      .concat(this.majorDisruptionTickets())
      .filter((ticket) => !this.isCurrentlyActive(ticket));
    return groupByAsSortedArray(inactiveTickets, grouper, sorter);
  });

  protected activeTicketsHeading = computed(
    () => this.majorDisruptionTickets().length > 0 || this.filteredTicketsActive().length > 0,
  );
  protected filterSubscriptionId = signal("");
  protected filterSubscriptionName = signal("");
  private filterProductType = signal("");
  protected hasFocusedSubscription = computed(() => this.filterSubscriptionId() !== "");
  protected hasFocusedProductType = computed(() => this.filterProductType() !== "");
  private focusProductTypeLightpath = computed(() => this.filterProductType() === "LightPath");
  private focusProductTypeIP = computed(() => this.filterProductType() === "IP");
  private focusProductTypeL2vpn = computed(() => this.filterProductType() === "L2VPN");
  private focusProductTypeFirewall = computed(() => this.filterProductType() === "Firewall");
  private focusProductTypePort = computed(() => this.filterProductType() === "Port");
  protected filterProductTypeKey = computed(() => {
    const key = this.focusProductTypeFirewall() ? "FW" : this.filterProductType();
    return subscriptionNameKey(key) ?? key;
  });
  protected hasTicketsAvailable = computed(() => this.filteredTickets().length > 0);
  private ticketsByActivityType = computed(() => {
    if (this.activeTypeFilters().length > 0) {
      return this.tickets().filter((ticket) => this.activeTypeFilters().includes(ticket.category));
    }
    return this.tickets();
  });
  private filteredTickets = computed(() => {
    let tickets: NotificationType[] = this.ticketsByActivityType();
    if (this.activeImpactFilters().length > 0) {
      tickets = tickets.filter((ticket) => this.checkImpactFilter(ticket));
    }

    return reverseSortTickets(tickets);
  });

  private isCurrentlyActive = (ticket: NotificationType) => {
    const now = dayjs();
    return dayjs(ticket.startDate).isBefore(now) && (!ticket.endDate || dayjs(ticket.endDate).isAfter(now));
  };

  constructor() {
    this.customerGuid = this.auth.viewingCustomer().customerId;

    this.auth.userLoaded.pipe(takeUntilDestroyed()).subscribe(() => {
      this.triggerFilter();
    });

    this.msgsrv.enableMalfunctionFilter$.pipe(takeUntilDestroyed()).subscribe((filter: MalfunctionFilterType) => {
      this.filterData.set(filter);
      this.triggerFilter();
    });

    this.msgsrv.enableFilter$.pipe(takeUntilDestroyed()).subscribe((filter) => {
      switch (filter.type) {
        case "type":
          this.activeTypeFilters.update((current) => [...current, filter.value]);
          break;
        case "impact":
          this.activeImpactFilters.update((current) => [...current, filter.value]);
          break;
        default:
          break;
      }
    });

    this.msgsrv.disableFilter$.pipe(takeUntilDestroyed()).subscribe((filter) => {
      switch (filter.type) {
        case "type":
          this.activeTypeFilters.update((current) => [...current.filter((value) => value !== filter.value)]);
          break;
        case "impact":
          this.activeImpactFilters.update((current) => [...current.filter((value) => value !== filter.value)]);
          break;
        default:
          break;
      }
    });
  }

  private triggerFilter() {
    if (!this.auth.viewingCustomer().customerId) {
      return;
    }

    const filterData = this.filterData();
    if (filterData) {
      this.filterSubscriptionId.set(filterData.subscriptionId);
      this.filterSubscriptionName.set(filterData.subscriptionName);
      this.filterProductType.set(filterData.productType);
      this.loadMoreActive = filterData.loadMoreActive;
      this.hasMore = filterData.hasMore ?? true;
      this.updateUrl();

      this.fetchNotifications();
    }
  }

  private updateUrl() {
    let newUrl = "storing-en-onderhoud";
    if (this.hasFocusedProductType()) {
      newUrl += "/" + this.filterProductType();
    }

    if (this.hasFocusedSubscription()) {
      newUrl += "/" + this.filterSubscriptionId();
    }

    this.location.replaceState(newUrl);
  }

  private processTicketsResult(ticketList: NotificationType[]) {
    if (this.loadMoreActive) {
      if (ticketList.length > 0) {
        // append
        this.tickets.update((current) => [...current, ...ticketList]);
      } else {
        this.hasMore = false; // no more data to load; disable load more button
      }
    } else {
      // replace result
      this.tickets.set(ticketList);
    }
    this.loading = false;
  }

  /**
   * Retrieve notifications from NWD-API.
   * Both regular tickets and major disruptions are fetched.
   */
  private async fetchNotifications() {
    this.loading = true;
    const customerId = this.auth.viewingCustomer().customerId;

    const filterMajorDisruptions: MajorDisruptionNotificationFilter = {
      customerId,
      beginTimestamp: this.filterData()?.filterStartDate,
      endTimestamp: this.filterData()?.filterEndDate,
    };

    const filter: NotificationFilter = {
      ...filterMajorDisruptions,
      accessCustomerId: customerId,
      impacts: [Impact.Down, Impact.ReducedRedundancy, Impact.ResilienceLoss, Impact.Unknown],
    };

    if (this.filterSubscriptionId() !== "") {
      filter.subscriptionIds = [this.filterSubscriptionId()];
    } else {
      filter.productTypes = this.filterProductType() !== "" ? [this.filterProductType()] : [];
    }

    const tickets = this.apollo.query<CimResult>({
      query: CIM_QUERY,
      variables: { filter },
      errorPolicy: "all",
      fetchPolicy: "network-only",
    });

    const majorDisruptions = this.apollo.query<CimMajorDisruptionResult>({
      query: CIM_MAJOR_DISRUPTION_QUERY,
      variables: { filter: filterMajorDisruptions },
      errorPolicy: "all",
      fetchPolicy: "network-only",
    });

    // combine both observables, map to a single array and process.
    combineLatest([tickets, majorDisruptions])
      .pipe(
        map(([ticketsData, disruptionsData]) => [
          ...(ticketsData.data?.notifications ?? []),
          ...(disruptionsData.data?.majorDisruptionNotifications ?? []),
        ]),
      )
      .subscribe((ticketList) => {
        this.processTicketsResult(ticketList);
      });
  }

  private checkActivityTypeFilter(item: NotificationType) {
    return this.activeTypeFilters().includes(item.category);
  }

  private checkImpactFilter(item: NotificationType) {
    return this.activeImpactFilters().filter((value) => item.tags?.includes(value)).length > 0;
  }
}
